import React, { useEffect, useState } from "react";
import { Loader2, GraduationCap, CheckCircle2, Clock, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function StaffPortal() {
  const { user } = useAuth();
  const [modules, setModules] = useState(null);
  const [completions, setCompletions] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [completing, setCompleting] = useState(false);

  const load = () => {
    api.get("/training/modules").then(r => setModules(r.data));
    api.get("/training/completions").then(r => setCompletions(r.data)).catch(() => setCompletions([]));
  };
  useEffect(() => { load(); }, []);

  const isCompleted = (moduleId) => completions.some(c => c.module_id === moduleId);

  const complete = async () => {
    setCompleting(true);
    try {
      await api.post("/training/completions", { module_id: viewing.id, score: 100 });
      toast.success("Module marked complete");
      setViewing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="p-8 lg:p-10" data-testid="page-staff-portal">
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Staff portal</p>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">Your training</h1>
        <p className="text-slate-500 mt-2">Complete assigned CPD modules to maintain your compliance certification.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="bg-white border border-slate-200 p-6" data-testid="staff-stat-total">
          <GraduationCap className="w-5 h-5 text-[#1FA59A] mb-6" strokeWidth={1.5} />
          <p className="font-display text-4xl font-bold text-slate-900">{modules?.length ?? "—"}</p>
          <p className="text-sm text-slate-500 mt-2">Available modules</p>
        </div>
        <div className="bg-white border border-slate-200 p-6" data-testid="staff-stat-completed">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-6" strokeWidth={1.5} />
          <p className="font-display text-4xl font-bold text-slate-900">{completions.length}</p>
          <p className="text-sm text-slate-500 mt-2">Completed</p>
        </div>
        <div className="bg-white border border-slate-200 p-6" data-testid="staff-stat-pending">
          <Clock className="w-5 h-5 text-amber-600 mb-6" strokeWidth={1.5} />
          <p className="font-display text-4xl font-bold text-slate-900">{(modules?.length ?? 0) - completions.length}</p>
          <p className="text-sm text-slate-500 mt-2">Outstanding</p>
        </div>
      </div>

      {modules === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map(m => {
            const done = isCompleted(m.id);
            return (
              <button key={m.id} onClick={() => setViewing(m)} className="text-left bg-white border border-slate-200 p-6 hover:border-slate-900 transition-colors flex flex-col" data-testid={`staff-module-${m.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <GraduationCap className="w-6 h-6 text-[#1FA59A]" strokeWidth={1.5} />
                  {done && <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-emerald-50 text-emerald-700">Completed</span>}
                </div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{m.category} · {m.duration_minutes} min</p>
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-3">{m.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed flex-1">{m.description}</p>
                <p className="mt-6 text-sm font-semibold text-[#1FA59A]">{done ? "Review module →" : "Start module →"}</p>
              </button>
            );
          })}
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setViewing(null)} data-testid="module-viewer">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl h-full overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex justify-between items-start">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{viewing.category} · {viewing.duration_minutes} min</p>
                <h2 className="font-display text-3xl font-bold text-slate-900">{viewing.title}</h2>
              </div>
              <button onClick={() => setViewing(null)} className="text-slate-400 hover:text-slate-900"><X /></button>
            </div>
            <div className="p-6 lg:p-10">
              <p className="text-lg text-slate-600 leading-relaxed mb-8">{viewing.description}</p>
              <div className="prose prose-slate max-w-none prose-headings:font-display" dangerouslySetInnerHTML={{ __html: viewing.content }} />
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 p-6 flex items-center justify-between">
              {isCompleted(viewing.id) ? (
                <p className="text-emerald-700 font-semibold flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Completed</p>
              ) : (
                <p className="text-sm text-slate-500">Mark as complete once you've read through the material.</p>
              )}
              <button onClick={complete} disabled={completing || isCompleted(viewing.id)} className="gs-gradient text-white font-semibold px-6 py-3 hover:opacity-90 disabled:opacity-50" data-testid="mark-complete">
                {completing ? "Saving…" : isCompleted(viewing.id) ? "Already completed" : "Mark complete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
